/**
 * BackOrdersGrid.jsx - Shared pieces for the storefront back-orders history grid.
 *
 * Mirrors OrdersGrid but for single-product back orders: read-only status tabs + a row per back
 * order. Used by both /back-orders (single store) and /account/back-orders (all stores).
 */
import { formatCurrency } from "../../utils/formatters";

// Status tabs (OData filters against /storefront-odata/StorefrontBackOrders) — one per back order status, plus All.
export const BACKORDER_STATUS_TABS = {
  open: {
    label: "Open",
    filter: "OptBackOrderStatusID eq 1",
    emptyMessage: "No open back orders",
    emptyIcon: "bi-clock-history",
  },
  partial: {
    label: "Partially Converted",
    filter: "OptBackOrderStatusID eq 2",
    emptyMessage: "No partially converted back orders",
    emptyIcon: "bi-pie-chart",
  },
  addedToCart: {
    label: "Added to Cart",
    filter: "OptBackOrderStatusID eq 5",
    emptyMessage: "No back orders added to cart",
    emptyIcon: "bi-cart-check",
  },
  completed: {
    label: "Completed",
    filter: "OptBackOrderStatusID eq 3",
    emptyMessage: "No completed back orders",
    emptyIcon: "bi-check-circle",
  },
  cancelled: {
    label: "Cancelled",
    filter: "OptBackOrderStatusID eq 4",
    emptyMessage: "No cancelled back orders",
    emptyIcon: "bi-x-circle",
  },
  all: {
    label: "All",
    filter: null,
    emptyMessage: "No back orders found",
    emptyIcon: "bi-clock-history",
  },
};

// Back order status badge (OptBackOrderStatus: 1 Open, 2 PartiallyConverted, 3 Completed,
// 4 Cancelled, 5 AddedToCart).
export const getBackOrderStatusInfo = (statusId) => {
  const statusMap = {
    1: { label: "Open", variant: "warning", icon: "bi-clock" },
    2: { label: "Partially Converted", variant: "info", icon: "bi-pie-chart" },
    3: { label: "Completed", variant: "success", icon: "bi-check-circle" },
    4: { label: "Cancelled", variant: "danger", icon: "bi-x-circle" },
    5: { label: "Added to Cart", variant: "primary", icon: "bi-cart-check" },
  };
  return statusMap[statusId] || { label: "Unknown", variant: "secondary", icon: "bi-question-circle" };
};

const variantName = (backOrder) =>
  backOrder?.productSkuVariant?.name ||
  backOrder?.productSkuVariant?.legacySKU ||
  `Variant #${backOrder?.productSkuVariantID}`;

// Read-only row for one back order (a back order is exactly one product).
export const BackOrderRow = ({ backOrder, showStore }) => {
  const status = getBackOrderStatusInfo(backOrder.optBackOrderStatusID);
  const flavor = backOrder.productSkuVariant?.flavor;
  const estTotal = (backOrder.unitQuantity || 0) * (backOrder.unitPrice || 0);

  return (
    <tr>
      <td>
        <span className="fw-semibold text-primary">#{backOrder.id}</span>
      </td>
      {showStore && (
        <td>
          <span className="badge bg-light text-dark">
            {backOrder.client?.name || "Unknown Store"}
          </span>
        </td>
      )}
      <td>
        <span className="text-muted">
          {backOrder.dateCreated
            ? new Date(backOrder.dateCreated).toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
                year: "numeric",
              })
            : "—"}
        </span>
      </td>
      <td>
        <div className="fw-medium">{variantName(backOrder)}</div>
        {flavor && <small className="text-muted d-block">{flavor}</small>}
      </td>
      <td className="text-center">
        <span className="badge bg-light text-dark">{backOrder.unitQuantity || 0} units</span>
      </td>
      <td className="text-end">
        <span className="fw-semibold">{formatCurrency(estTotal)}</span>
      </td>
      <td className="text-center">
        <span className={`badge bg-${status.variant}`}>
          <i className={`bi ${status.icon} me-1`}></i>
          {status.label}
        </span>
      </td>
    </tr>
  );
};
